/**
 * Video Cover Block - Interactivity API View Script
 *
 * Provides reactive video controls using WordPress Interactivity API.
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from "@wordpress/interactivity";

/**
 * Where each provider's player frame actually lives.
 *
 * postMessage with "*" hands the message to whatever document occupies the
 * frame, which is not necessarily the one that was embedded. Naming the origin
 * costs nothing and means a command can only ever reach the intended player.
 */
const PLAYER_ORIGINS = {
  youtube: "https://www.youtube.com",
  vimeo: "https://player.vimeo.com",
};

/**
 * A looping background video is the moving content this preference exists for.
 */
const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Send a command to an embedded player, if the frame is there to receive it.
 *
 * @param {HTMLElement} wrapper   Block wrapper.
 * @param {string}      videoType Provider key.
 * @param {string}      message   Serialized command.
 * @return {boolean} Whether the message was posted.
 */
const postToPlayer = (wrapper, videoType, message) => {
  const iframe = wrapper.querySelector(".brandy-video-cover-iframe");
  const origin = PLAYER_ORIGINS[videoType];

  if (!iframe || !iframe.contentWindow || !origin) {
    return false;
  }

  iframe.contentWindow.postMessage(message, origin);
  return true;
};

/**
 * Video Cover block store.
 *
 * Uses WordPress Interactivity API for reactive state management.
 */
store("brandy/video-cover", {
  /**
   * Derived accessible names. The label states the action the press performs,
   * which is the part a screen reader user needs; the strings come from the
   * server so they are translatable.
   */
  state: {
    get playPauseLabel() {
      const ctx = getContext();
      return ctx.isPlaying ? ctx.pauseLabel : ctx.playLabel;
    },
    get muteLabel() {
      const ctx = getContext();
      return ctx.isMuted ? ctx.unmuteLabel : ctx.muteLabel;
    },
  },

  /**
   * Actions for user interactions.
   */
  actions: {
    /**
     * Toggle video play/pause state.
     */
    togglePlay() {
      const ctx = getContext();
      const { ref } = getElement();
      const wrapper = ref.closest(".brandy-video-cover");

      if (ctx.videoType === "upload") {
        const video = wrapper.querySelector(".brandy-video-cover-video");
        if (video) {
          if (ctx.isPlaying) {
            video.pause();
          } else {
            video.play();
          }
          ctx.isPlaying = !ctx.isPlaying;
        }
      } else if (ctx.videoType === "youtube") {
        const func = ctx.isPlaying ? "pauseVideo" : "playVideo";
        const sent = postToPlayer(
          wrapper,
          "youtube",
          `{"event":"command","func":"${func}","args":""}`
        );
        // Only claim the new state if the command actually went out, so the
        // icon never contradicts the player.
        if (sent) {
          ctx.isPlaying = !ctx.isPlaying;
        }
      } else if (ctx.videoType === "vimeo") {
        const method = ctx.isPlaying ? "pause" : "play";
        if (postToPlayer(wrapper, "vimeo", `{"method":"${method}"}`)) {
          ctx.isPlaying = !ctx.isPlaying;
        }
      }
    },

    /**
     * Toggle video mute/unmute state.
     */
    toggleMute() {
      const ctx = getContext();
      const { ref } = getElement();
      const wrapper = ref.closest(".brandy-video-cover");

      if (ctx.videoType === "upload") {
        const video = wrapper.querySelector(".brandy-video-cover-video");
        if (video) {
          video.muted = !video.muted;
          ctx.isMuted = video.muted;
        }
      } else if (ctx.videoType === "youtube") {
        const func = ctx.isMuted ? "unMute" : "mute";
        const sent = postToPlayer(
          wrapper,
          "youtube",
          `{"event":"command","func":"${func}","args":""}`
        );
        if (sent) {
          ctx.isMuted = !ctx.isMuted;
        }
      } else if (ctx.videoType === "vimeo") {
        const volume = ctx.isMuted ? 1 : 0;
        if (
          postToPlayer(
            wrapper,
            "vimeo",
            `{"method":"setVolume","value":${volume}}`
          )
        ) {
          ctx.isMuted = !ctx.isMuted;
        }
      }
    },
  },

  /**
   * Callbacks run on specific lifecycle events.
   */
  callbacks: {
    /**
     * Initialize the video player.
     * Called via data-wp-init directive when block is rendered.
     */
    initVideo() {
      const ctx = getContext();
      const { ref } = getElement();

      /*
       * The markup carries the real autoplay attributes so the block still
       * behaves with the view module absent — which means a reader who asked
       * for less motion is stopped here rather than never started. The
       * controls stay exactly where they are, so the video remains one press
       * away.
       */
      const stopForReducedMotion = ctx.autoplay && prefersReducedMotion();

      if (ctx.videoType === "upload") {
        const video = ref.querySelector(".brandy-video-cover-video");
        if (video) {
          // Listen for play/pause events to sync state.
          video.addEventListener("play", () => {
            ctx.isPlaying = true;
          });

          video.addEventListener("pause", () => {
            ctx.isPlaying = false;
          });

          if (stopForReducedMotion) {
            video.pause();
            ctx.isPlaying = false;
          } else if (ctx.autoplay && ctx.isMuted) {
            video.play().catch(() => {
              // Autoplay failed (browser restriction).
              ctx.isPlaying = false;
            });
          }
        }
      } else if (ctx.videoType === "youtube" || ctx.videoType === "vimeo") {
        /*
         * Embedded players can start and stop on their own — an ad, a
         * buffering stall, the reader clicking the frame itself. Without a
         * return channel the icon only ever reflected what this code last
         * asked for, so a video the reader paused elsewhere still showed a
         * pause button. Both providers will report their state, but only
         * after being asked to.
         */
        const iframe = ref.querySelector(".brandy-video-cover-iframe");
        const origin = PLAYER_ORIGINS[ctx.videoType];

        const onPlayerMessage = (event) => {
          // A frame is not proof of a sender; anything can post to this window.
          if (event.origin !== origin || !iframe || event.source !== iframe.contentWindow) {
            return;
          }

          let data = event.data;
          if (typeof data === "string") {
            try {
              data = JSON.parse(data);
            } catch {
              return;
            }
          }
          if (!data || typeof data !== "object") {
            return;
          }

          if (ctx.videoType === "youtube") {
            // info.playerState: 1 playing, 2 paused, 0 ended.
            const playerState = data.info?.playerState;
            if (playerState === 1) {
              ctx.isPlaying = true;
            } else if (playerState === 2 || playerState === 0) {
              ctx.isPlaying = false;
            }
          } else if (data.event === "play") {
            ctx.isPlaying = true;
          } else if (data.event === "pause" || data.event === "ended") {
            ctx.isPlaying = false;
          }
        };

        window.addEventListener("message", onPlayerMessage);

        /*
         * Both providers need a subscription before they send anything, and
         * neither accepts one before the frame has loaded. Sending on load —
         * and once immediately, in case the frame was already there — covers
         * both orders without polling.
         */
        const subscribe = () => {
          if (ctx.videoType === "youtube") {
            postToPlayer(ref, "youtube", '{"event":"listening"}');
          } else {
            ["play", "pause", "ended"].forEach((eventName) => {
              postToPlayer(
                ref,
                "vimeo",
                `{"method":"addEventListener","value":"${eventName}"}`
              );
            });
          }

          if (stopForReducedMotion) {
            const paused =
              ctx.videoType === "youtube"
                ? postToPlayer(
                    ref,
                    "youtube",
                    '{"event":"command","func":"pauseVideo","args":""}'
                  )
                : postToPlayer(ref, "vimeo", '{"method":"pause"}');

            if (paused) {
              ctx.isPlaying = false;
            }
          }
        };

        iframe?.addEventListener("load", subscribe);
        subscribe();

        return () => {
          window.removeEventListener("message", onPlayerMessage);
          iframe?.removeEventListener("load", subscribe);
        };
      }

      // Return cleanup function.
      return () => {
        // Cleanup if needed.
      };
    },
  },
});
